import type { ReviewNoteView } from '@/api/lcReviewNote'
import type { UaDetail } from '@/api/unitAssessmentAdmin'
import type {
  ComponentProgressDetailView,
  LcTestDetailView,
  ManualGradeCredit,
  PauseQuizResponseView,
} from '@/types/coaching'
import { hasSubmittedAnswer, parseStoredAnswer } from '../component-details/parts/answerArray'
import {
  displayCorrectAnswer,
  hasDisplayableCorrectAnswer,
  snapshotText,
} from '../component-details/parts/QuestionResultCard'
import { isSubjectiveQuizType } from '../component-details/parts/SubjectiveGradeSelect'
import type { GradingDraft } from '@/store/gradingDraftStore'

/**
 * 채점표 행 모델 — 블록퀴즈(pause)·리뷰노트·챕터/누적 테스트·단원평가의 공용 표 정규화.
 * unanswered: 응답 행 없음 / pending: 응답은 있으나 채점 대기(isCorrect null) / 나머지: 채점됨.
 */
export type GradingRowStatus = 'unanswered' | 'pending' | 'correct' | 'partial' | 'incorrect'

export interface GradingSheetRow {
  /** 채점표 내부 고유키 — quizId(Snowflake String) 그대로. 숫자 변환 금지. */
  key: string
  /** 1-based 표시 번호 */
  no: number
  /** 표시 라벨. 비어 있으면 컴포넌트가 `gradingMode.rowLabel`("{n}번")로 채운다. 채점표는 워크북처럼 번호만 쓴다. */
  label: string
  /** 문제 1줄 요약 */
  stem: string | null
  /** 사람이 읽는 정답 문자열. 표시 불가 유형이면 null → "정답 없음" */
  correctAnswerText: string | null
  /** 서버 채점 상태를 O/△/X 값으로 정규화한 현재값 */
  current: ManualGradeCredit | null
  status: GradingRowStatus
  hasStudentAnswer: boolean
  /** 서술형(explanation) 블록퀴즈만 true — 리뷰노트는 백엔드에 partialCredit이 없어 항상 false */
  allowPartial: boolean
  questionSnapshot: unknown
  correctAnswer: unknown
  explanation: string | null
  /** pause 행만 — 저장 요청의 pauseTimeMs */
  pauseTimeMs?: number
}

/** 행별 교사 입력. 키가 없으면 미변경(현재값 표시). */
export type { GradingDraft } from '@/store/gradingDraftStore'

export function normalizePauseCurrent(
  response: PauseQuizResponseView | undefined,
): { current: ManualGradeCredit | null; status: GradingRowStatus } {
  if (!response) return { current: null, status: 'unanswered' }
  if (response.isCorrect == null) return { current: null, status: 'pending' }
  if (response.partialCredit != null && response.partialCredit > 0 && response.partialCredit < 1) {
    return { current: 0.5, status: 'partial' }
  }
  return response.isCorrect
    ? { current: 1, status: 'correct' }
    : { current: 0, status: 'incorrect' }
}

export function normalizeReviewNoteCurrent(
  gradedIsCorrect: boolean | null,
): { current: ManualGradeCredit | null; status: GradingRowStatus } {
  if (gradedIsCorrect == null) return { current: null, status: 'unanswered' }
  return gradedIsCorrect
    ? { current: 1, status: 'correct' }
    : { current: 0, status: 'incorrect' }
}

function correctAnswerTextOf(correctAnswer: unknown, snapshot: unknown): string | null {
  return hasDisplayableCorrectAnswer(correctAnswer)
    ? displayCorrectAnswer(correctAnswer, snapshot)
    : null
}

/** video 유닛의 pause point를 시각 순으로 채점표 행으로 만든다. pause point가 없으면 빈 배열. */
export function pauseRowsOf(component: ComponentProgressDetailView): GradingSheetRow[] {
  const video = component.video
  if (!video) return []
  const responsesByQuiz = new Map((video.pauseQuizResponses ?? []).map((r) => [r.quizId, r]))
  const orderedPoints = [...(video.pausePoints ?? [])].sort((a, b) => a.pauseTimeMs - b.pauseTimeMs)

  return orderedPoints.map((point, index) => {
    const response = responsesByQuiz.get(point.quizId)
    const { current, status } = normalizePauseCurrent(response)
    return {
      key: point.quizId,
      no: index + 1,
      label: '',
      stem: snapshotText(point.questionSnapshot),
      correctAnswerText: correctAnswerTextOf(point.correctAnswer, point.questionSnapshot),
      current,
      status,
      hasStudentAnswer: hasSubmittedAnswer(response?.studentAnswer),
      allowPartial: isSubjectiveQuizType(point.questionSnapshot),
      questionSnapshot: point.questionSnapshot,
      correctAnswer: point.correctAnswer,
      explanation: point.explanation ?? null,
      pauseTimeMs: point.pauseTimeMs,
    }
  })
}

/** 리뷰노트 세트 문항을 items 순서로 채점표 행으로 만든다. △ 없음(O/X만). */
export function reviewNoteRowsOf(reviewNote: ReviewNoteView): GradingSheetRow[] {
  return reviewNote.items.map((item, index) => {
    const { current, status } = normalizeReviewNoteCurrent(item.gradedIsCorrect)
    return {
      key: item.quizId,
      no: index + 1,
      label: '',
      stem: snapshotText(item.questionSnapshot),
      correctAnswerText: correctAnswerTextOf(item.correctAnswer, item.questionSnapshot),
      current,
      status,
      hasStudentAnswer: hasSubmittedAnswer(parseStoredAnswer(item.answer)),
      allowPartial: false,
      questionSnapshot: item.questionSnapshot,
      correctAnswer: item.correctAnswer,
      explanation: item.explanation,
    }
  })
}

/**
 * 챕터/누적 테스트 문항을 sortOrder 순으로 채점표 행으로 만든다.
 * 백엔드 `lc_test_item`에 partialCredit이 없어 리뷰노트처럼 O/X만(△ 없음).
 * 정오답 정규화는 리뷰노트와 같은 `gradedIsCorrect` boolean 규칙을 쓴다.
 */
export function testRowsOf(test: LcTestDetailView): GradingSheetRow[] {
  const ordered = [...test.items].sort((a, b) => a.sortOrder - b.sortOrder)
  return ordered.map((item, index) => {
    const { current, status } = normalizeReviewNoteCurrent(item.gradedIsCorrect)
    return {
      key: item.quizId,
      no: index + 1,
      label: '',
      stem: snapshotText(item.questionSnapshot),
      correctAnswerText: correctAnswerTextOf(item.correctAnswer, item.questionSnapshot),
      current,
      status,
      hasStudentAnswer: hasSubmittedAnswer(parseStoredAnswer(item.answer)),
      allowPartial: false,
      questionSnapshot: item.questionSnapshot,
      correctAnswer: item.correctAnswer,
      explanation: item.explanation,
    }
  })
}

/** 단원평가 문항을 sortOrder 순 O/X 채점 행으로 만든다. 학생 답안은 표시용으로만 읽는다. */
export function unitAssessmentRowsOf(assessment: UaDetail): GradingSheetRow[] {
  const ordered = [...assessment.items].sort((a, b) => a.sortOrder - b.sortOrder)
  return ordered.map((item, index) => {
    const { current, status } = normalizeReviewNoteCurrent(item.gradedIsCorrect)
    return {
      key: item.quizId,
      no: index + 1,
      label: '',
      stem: snapshotText(item.questionSnapshot),
      correctAnswerText: correctAnswerTextOf(item.correctAnswer, item.questionSnapshot),
      current,
      status,
      hasStudentAnswer: hasSubmittedAnswer(parseStoredAnswer(item.answer)),
      allowPartial: false,
      questionSnapshot: item.questionSnapshot,
      correctAnswer: item.correctAnswer,
      explanation: item.explanation,
    }
  })
}

/** 화면에 표시되는 값 = draft 우선, 없으면 현재값. */
export function displayedGrade(row: GradingSheetRow, draft: GradingDraft): ManualGradeCredit | null {
  return draft[row.key] ?? row.current
}

/**
 * 토글 규칙: 표시값과 같은 값을 다시 누르면 draft를 지워 현재값으로 복귀(draft 단계 해제),
 * 다르면 설정. 현재값과 같은 값으로 돌아오면 변경 없음이므로 역시 지운다.
 */
export function toggleDraft(
  draft: GradingDraft,
  row: GradingSheetRow,
  grade: ManualGradeCredit,
): GradingDraft {
  const shown = displayedGrade(row, draft)
  const next: Record<string, ManualGradeCredit> = { ...draft }
  if (shown === grade || row.current === grade) {
    delete next[row.key]
  } else {
    next[row.key] = grade
  }
  return next
}

/** 저장 대상 = draft가 있고 현재값과 다른 행(표시 순서 유지). */
export function changedRows(rows: GradingSheetRow[], draft: GradingDraft): GradingSheetRow[] {
  return rows.filter((row) => draft[row.key] !== undefined && draft[row.key] !== row.current)
}
